/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { createDefaultGridModel, ROW_DIRTY_CLASS } from '@gh/common-internal-shared';
import { NumberTypes, State, TextTypes } from '@gh/common-shared';
import { TranslationService } from '@gh/core-mls';
import {
  buildEditableNumberColumn,
  buildEditableTextColumn,
  gridCollectionComparator,
  GridModel,
  GridOptionsExt,
  ManualGridDefinitionWithModel,
} from '@gh/core-ui-grid';
import { ColDef } from 'ag-grid-community';
import { isNil } from 'lodash';
import {
  getHpeCuttingListItemId,
  HpeCuttingListItem,
  hpeCuttingListItemFieldState,
  HpeUomRefItem,
  validateHpeCuttingListItemField,
} from '../../../resources/hpe-details.model';
import { HpeCommandService } from '../hpe-command.service';
import { hpeCuttingListComparator, HpeDetailsState, queryHpeEditable } from '../hpe-details.state';

@Injectable()
export class HpeDetailsCuttingGridDefinitionFactory {
  constructor(private translationService: TranslationService,
              private state: State<HpeDetailsState>,
              private commands: HpeCommandService) {

  }

  create(uom: HpeUomRefItem): HpeDetailsCuttingGridDefinition {
    return new HpeDetailsCuttingGridDefinition(this.translationService, this.state, this.commands, uom);
  }
}

export class HpeDetailsCuttingGridDefinition extends ManualGridDefinitionWithModel {
  constructor(private translationService: TranslationService,
              private state: State<HpeDetailsState>,
              private commands: HpeCommandService,
              private uom: HpeUomRefItem) {
    super();
  }

  buildGridModel(): GridModel {
    return createDefaultGridModel<HpeCuttingListItem>({
      editable: this.state.project(queryHpeEditable),
      getId: getHpeCuttingListItemId,
      validate: validateHpeCuttingListItemField,
      getCellDef: hpeCuttingListItemFieldState,
      command: (entity, name, value) => this.commands.cuttingListItemSetField(
        entity.sourceType,
        entity.sourceId,
        getHpeCuttingListItemId(entity),
        name,
        value),
    });
  }

  buildGridOptions(): GridOptionsExt {
    return {
      defaultColDef: {
        sortable: false,
      },
      rowClassRules: {
        [ROW_DIRTY_CLASS]: ({ data }: { data: HpeCuttingListItem }) => !isNil(data.dirtyType),
      },
    };
  }

  buildColumnDefs(): ColDef[] {
    const { usePieces, dimension1, dimension2, dimension3 } = this.uom;

    const uomColumns: ColDef[] = [];

    if (usePieces) {
      uomColumns.push({
        field: 'pieces',
        headerName: this.translationService.get('lbl_uom_pieces'),
        width: 100,
        ...buildEditableNumberColumn(NumberTypes.QuantityIntegral, this.model.getCellAccessor('pieces')),
      });
    }
    if (dimension1) {
      uomColumns.push({
        field: 'dimension1Value',
        headerName: dimension1,
        width: 100,
        ...buildEditableNumberColumn(NumberTypes.Dimension, this.model.getCellAccessor('dimension1Value')),
      });
    }
    if (dimension2) {
      uomColumns.push({
        field: 'dimension2Value',
        headerName: dimension2,
        width: 100,
        ...buildEditableNumberColumn(NumberTypes.Dimension, this.model.getCellAccessor('dimension2Value')),
      });
    }
    if (dimension3) {
      uomColumns.push({
        field: 'dimension3Value',
        headerName: dimension3,
        width: 100,
        ...buildEditableNumberColumn(NumberTypes.Dimension, this.model.getCellAccessor('dimension3Value')),
      });
    }

    return [{
      field: 'description',
      headerName: this.translationService.get('lbl_description'),
      width: 400,
      ...buildEditableTextColumn(TextTypes.Description, this.model.getCellAccessor('description')),
      sort: 'asc',
      comparator: gridCollectionComparator(hpeCuttingListComparator),
    }, ...uomColumns, {
      field: 'quantity',
      headerName: this.translationService.get('lbl_quantity'),
      width: 100,
    }];
  }
}
